/** One choice in a SearchPicker: the value it sets and the text it shows. */
export interface PickOption {
  label: string;
  value: string;
}

/** A headed group of choices, for a picker that lists more than one kind of thing. */
export interface PickGroup {
  text: string;
  options: PickOption[];
}

/** How many matches each group shows; Forge's option limit is not applied per group. */
export const GROUP_LIMIT = 25;

/** Case-insensitive match on the label or the value, so either the shown text or the id finds an option. */
export function matchOptions(options: PickOption[], text: string): PickOption[] {
  const t = text.trim().toLowerCase();
  return t ? options.filter((o) => o.label.toLowerCase().includes(t) || o.value.toLowerCase().includes(t)) : options;
}

/**
 * The picker's filter. Forge also calls it with empty text and the chosen value to show that
 * value's label, so with no text the chosen option comes first and cannot fall past the limit.
 */
export function pick(options: PickOption[], text: string, value: string | null): PickOption[] {
  const found = matchOptions(options, text);
  if (text.trim() || !value) return found;
  const chosen = found.find((o) => o.value === value);
  return chosen ? [chosen, ...found.filter((o) => o !== chosen)] : found;
}

/** `pick` for grouped options: each group matched and capped, empty groups dropped. */
export function pickGrouped(groups: PickGroup[], text: string, value: string | null): PickGroup[] {
  return groups
    .map((g) => ({ text: g.text, options: pick(g.options, text, value).slice(0, GROUP_LIMIT) }))
    .filter((g) => g.options.length > 0);
}
